import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
// Iconsax — ClusterDen's standard icon library (see [[icon-library-iconsax]]); DocumentText1
// drives the colored file-type badge in the redesigned card below.
import { DocumentText1 } from "iconsax-react";
import { formatHumanMediaBytes } from "../../../../utils/formatHumanMediaBytes";
import {
  resolveDocumentDisplayTitle,
  stripWhatsAppWorkspaceDocumentUrlBasename,
  stripClusterDenDocumentsStorageUniqueSuffix,
} from "../../../../utils/documentDisplayName";
import { useEchoMediaFetch } from "./useEchoMediaFetch";

// File-type accent for the badge — same semantic-color-by-category convention common chat
// apps use (red PDF, blue Word doc, green spreadsheet, ...) instead of one flat grey icon for
// every file type.
const EXT_ACCENT_COLORS = {
  PDF: "#DC2626",
  DOC: "#2563EB",
  DOCX: "#2563EB",
  XLS: "#16A34A",
  XLSX: "#16A34A",
  CSV: "#16A34A",
  PPT: "#EA580C",
  PPTX: "#EA580C",
  ZIP: "#7C3AED",
  RAR: "#7C3AED",
};
const extAccentColor = (ext) => EXT_ACCENT_COLORS[ext] || "#64748B";

// Same fixed-card-width convention as CatalogMessageCard, at its widest cap (380px) — first
// tried List's 340px, Rahim: "feels like a lot small" — bumped to match Catalog since a
// single-row icon+name+size card reads smaller than its width even more than a multi-row card
// does (Rahim, 2026-08-27: "dont use extremmly small card use our basic card size").
const documentCardWidthSx = {
  maxWidth: {
    xs: "min(calc((var(--chatbox-width, 100vw)) * 0.6), calc(100vw - 72px))",
    sm: "min(380px, calc(var(--chatbox-width, 100vw) - 96px))",
  },
  width: { xs: "100%", sm: "auto" },
  boxSizing: "border-box",
};

const DocumentBox = ({ documentUrl, documentFileName, mediaThumbnailUrl, mediaFileSizeBytes, messagePlatform, contactId, messageId, metaMediaId }) => {
  const { resolvedUrl, isFetching, handleFetch } = useEchoMediaFetch(documentUrl, {
    contactId,
    messageId,
    tag: "DocumentBox",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const documentUrl_ = resolvedUrl;

  const isLocalDocument = documentUrl_ instanceof File;

  const finalDocumentUrl = useMemo(() => {
    if (isLocalDocument) {
      return URL.createObjectURL(documentUrl_);
    }
    return documentUrl_;
  }, [documentUrl_, isLocalDocument]);

  useEffect(() => {
    return () => {
      if (isLocalDocument && finalDocumentUrl && String(finalDocumentUrl).startsWith("blob:")) {
        try {
          URL.revokeObjectURL(finalDocumentUrl);
        } catch (_) {}
      }
    };
  }, [isLocalDocument, finalDocumentUrl]);

  if (!resolvedUrl && metaMediaId) {
    // Redesigned (Rahim, 2026-08-27, "make this better" against a screenshot): dropped the
    // big grey ChatMediaCollapsedPreview box — documents don't have a real visual thumbnail
    // (that gate is meant for photo/video), so it always rendered as an empty placeholder
    // with a duplicate download affordance next to the filename below it. One compact
    // colored file-badge + name + a single tap-to-load action instead.
    const pendingExtMatch = String(documentFileName || "").match(/\.([a-zA-Z0-9]+)$/);
    const pendingExt = pendingExtMatch ? pendingExtMatch[1].toUpperCase() : "FILE";
    return (
      <Box
        onClick={!isFetching ? handleFetch : undefined}
        sx={{
          ...documentCardWidthSx,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderRadius: "12px",
          border: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: "background.paper",
          px: 2,
          py: 1.75,
          cursor: isFetching ? "default" : "pointer",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 52,
            height: 52,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${extAccentColor(pendingExt)}1A`,
            color: extAccentColor(pendingExt),
          }}
        >
          <DocumentText1 size={28} color="currentColor" variant="Bold" />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem", wordBreak: "break-word" }}>
            {documentFileName || "Document"}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
            Tap to load
          </Typography>
        </Box>
        {isFetching ? (
          <CircularProgress size={24} sx={{ flexShrink: 0 }} />
        ) : (
          <FileDownloadOutlinedIcon sx={{ color: "text.secondary", flexShrink: 0, fontSize: "1.5rem" }} />
        )}
      </Box>
    );
  }

  const getDocumentName = (document, platform) => {
    if (document instanceof File) {
      return document.name;
    }
    if (typeof document === "string") {
      const parts = document.split("/");
      let filename = parts[parts.length - 1] || "document";
      filename = filename.split("?")[0];
      try {
        filename = decodeURIComponent(filename);
      } catch (_) {}
      if (platform === "whatsapp" || platform === "qr") {
        filename = stripWhatsAppWorkspaceDocumentUrlBasename(document, filename);
      }
      return filename;
    }
    return "Unknown Document";
  };

  const documentName = resolveDocumentDisplayTitle(documentUrl_, documentFileName, {
    platform: messagePlatform,
  });
  const rawForExt =
    (documentFileName && String(documentFileName).trim() && (messagePlatform === "whatsapp" || messagePlatform === "qr")
      ? stripClusterDenDocumentsStorageUniqueSuffix(String(documentFileName).trim())
      : documentFileName) ||
    (isLocalDocument ? documentUrl_.name : getDocumentName(documentUrl_, messagePlatform));
  const extMatch = String(rawForExt).match(/\.([a-zA-Z0-9]+)(\?|$)/);
  const extLabel = extMatch ? extMatch[1].toUpperCase() : "FILE";
  const sizeLabel = formatHumanMediaBytes(
    mediaFileSizeBytes != null && Number.isFinite(Number(mediaFileSizeBytes))
      ? Number(mediaFileSizeBytes)
      : isLocalDocument && documentUrl_.size != null
        ? documentUrl_.size
        : null
  );
  const metaLine = [sizeLabel || null, extLabel].filter(Boolean).join(" • ");

  const openDocument = () => {
    setIsLoading(true);
    setHasError(false);
    try {
      window.open(finalDocumentUrl, "_blank", "noopener,noreferrer");
    } catch (_) {
      setHasError(true);
    }
    setTimeout(() => setIsLoading(false), 600);
  };

  // Redesigned (Rahim, 2026-08-27, "make this better" against a screenshot): dropped the big
  // grey ChatMediaCollapsedPreview thumbnail box entirely for the common case (no real
  // `mediaThumbnailUrl`) — documents don't have a visual preview the way photos/videos do, so
  // it always rendered as an empty grey placeholder with a generic broken-image-style icon
  // AND a second, redundant download button right below it. One compact row instead: a
  // colored file-type badge, name + size/type, and a single download action. When a real
  // thumbnail URL genuinely exists, still show it as a slim strip above that row rather than
  // the old oversized empty-most-of-the-time box.
  const badgeColor = extAccentColor(extLabel);

  return (
    <Box
      sx={{
        ...documentCardWidthSx,
        borderRadius: "12px",
        overflow: "hidden",
        // Fixed light border to match — theme.palette.divider in dark mode is a
        // light-on-dark tone that would go almost invisible against this card
        // now that it's fixed white instead of following the theme.
        border: "1px solid #DADCDE",
        // Fixed white regardless of theme (Rahim, 2026-08-27: "pdf is black make it
        // white") — a document preview reads as a physical paper card, same call
        // already made for Template's own inner card (CARD_BG stays white always).
        bgcolor: "#fff",
      }}
    >
      {mediaThumbnailUrl && (
        <Box
          component="img"
          src={mediaThumbnailUrl}
          alt=""
          sx={{ width: "100%", height: 110, objectFit: "cover", display: "block" }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      )}
      {/* Content bumped up a size across the board (Rahim, 2026-08-27: "still feels like the
          card and its content a little too small") — badge 40→52px/icon 22→28, name/meta text
          sizes raised, roomier padding, bigger download target. Width stays at Catalog's 380px
          cap (already our widest card), so this is purely about the content filling it out. */}
      <Box
        onClick={openDocument}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.75,
          cursor: "pointer",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 52,
            height: 52,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${badgeColor}1A`,
            color: badgeColor,
          }}
        >
          <DocumentText1 size={28} color="currentColor" variant="Bold" />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {/* Fixed dark text — the card itself is fixed white now (not mode-aware),
              so theme-driven "text.secondary" tokens (light in dark mode) would go
              near-invisible here. Same #050505/#65676B pair CatalogMessageCard's own
              fixed-light header section already uses, for consistency. */}
          <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem", wordBreak: "break-word", color: "#050505" }}>
            {documentName}
          </Typography>
          {metaLine ? (
            <Typography sx={{ display: "block", mt: 0.375, fontSize: "0.8125rem", color: "#65676B" }}>
              {hasError ? "Couldn't open — tap to retry" : metaLine}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openDocument();
          }}
          aria-label="Download document"
          sx={{ flexShrink: 0 }}
        >
          {isLoading ? (
            <CircularProgress size={24} />
          ) : (
            <FileDownloadOutlinedIcon sx={{ color: "#65676B", fontSize: "1.5rem" }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default DocumentBox;
